import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { StatsAPI } from "@shared/types/api";
import { api } from "@/lib/api";

interface InterstitialProps {
  questionId: string;
  answer: string | string[] | number;
  onComplete?: () => void;
}

export default function Interstitial({ questionId, answer }: InterstitialProps) {
  const [statMessage, setStatMessage] = useState<{ emoji: string; message: string } | null>(null);

  // Fetch interstitial message from server
  useEffect(() => {
    let isMounted = true;

    const fetchInterstitial = async () => {
      try {
        const response = await api.post<StatsAPI.InterstitialResponseData>("/stats/quiz/interstitial", {
          questionId,
          answer,
        });

        if (isMounted) {
          if (response.success && response.data) {
            setStatMessage(response.data);
          } else {
            setStatMessage({ emoji: "✨", message: "You're in good company!" });
          }
        }
      } catch (error) {
        console.error("Failed to fetch interstitial:", error);
        if (isMounted) {
          setStatMessage({ emoji: "✨", message: "You're in good company!" });
        }
      }
    };

    fetchInterstitial();

    return () => {
      isMounted = false;
    };
  }, [questionId, answer]);

  if (!statMessage) return null;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.2, ease: "easeOut" }}
    >
      <div className="card rounded-2xl p-6 border border-primary-500/20">
        <div className="flex items-center gap-4">
          <motion.span
            className="text-3xl flex-shrink-0"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", damping: 10, stiffness: 200 }}
          >
            {statMessage.emoji}
          </motion.span>
          <p className="text-dark-text font-medium">
            {statMessage.message}
          </p>
        </div>
      </div>
    </motion.div>
  );
}
